import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import { makeStyles } from '@material-ui/core/styles';
import { useIntl } from 'react-intl';
import { Box } from '@material-ui/core';
import _ from 'lodash';
import { MEDIA_TYPE } from '../../common/Constants';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import ImgDialog from '../../components/Dialog/ImgDialog';
import Button from '../../components/Button/CustomButton';
import SelectorInput from '../../components/Input/SelectorInput';
import Snackbar from '../../components/Snackbar/Snackbar';
import CustomTypography from '../../components/Typography/CustomTypography';

const useStyles = makeStyles(() => ({
    boxPlaceholder: {
        height: '400px',
    },
    imgLightbox: {
        display: 'block',
        maxWidth: '100%',
        maxHeight: '400px',
        margin: 'auto',
    },
    btnStyle: {
        fontSize: 14,
        minWidth: '100px',
        margin: '10px',
    },
    priceSection: {
        margin: '30px 0',
        fontWeight: 'bold',
        color: (props) => props?.settings_colors_viewer?.contents_text_color,
    },
    priceLabel: {
        marginRight: '15px',
    },
    chooseProduct: {
        display: 'flex',
        alignItems: 'center',
        '& > div': {
            width: '60px',
            '& > div': {
                backgroundColor: '#fff',
            },
        },
    },
    chooseNoProduct: {
        display: 'flex',
        alignItems: 'center',
        '& > div': {
            width: '60px',
            '& > div > select': {
                color: 'rgb(196 196 196)',
            },
            '& > div > svg': {
                color: 'rgb(196 196 196)',
            },
            '& > div': {
                backgroundColor: '#fff',
            },
        },
        pointerEvents: 'none',
    },
    noProduct: {
        color: 'red',
        fontSize: '20px',
        marginRight: '10px',
    },
    productDetailName: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: (props) => props?.settings_colors_viewer?.contents_text_color,
    },
    productDescription: {
        marginTop: '20px',
        fontSize: '20px',
        color: (props) => props?.settings_colors_viewer?.contents_text_color,
    },
}));

const GrandBoothProductItem = ({
    productDetail,
    outsideLinkUrl,
    token,
    isAuthorized,
    nextProduct,
    prevProduct,
    setOpenBusinessContactForm,
    productActiveIndex,
    totalProducts,
    infoAddToCart,
    addProductToCart,
    clearAddToCart,
    isWeakAuth,
    eventInfo,
    getCount,
    customText,
    isPreview,
}) => {
    const classes = useStyles(eventInfo);
    const intl = useIntl();
    const [openDialogImg, setOpenDialogImg] = useState(false);
    const [selectQuantity, setSelectQuantity] = useState(1);
    const [snackbar, setSnackbar] = useState({ isOpen: false, type: 'success', message: '' });

    useEffect(() => {
        if (!_.isEmpty(infoAddToCart.data)) {
            if (infoAddToCart.success) {
                getCount();
                setSnackbar({ isOpen: true, type: 'success', message: intl.formatMessage({ id: 'exhibition.addedToCart' }) });
            }
            clearAddToCart();
        }
        if (!_.isEmpty(infoAddToCart.errors)) {
            setSnackbar({ isOpen: true, type: 'error', message: infoAddToCart.errors.message.message });
            clearAddToCart();
        }
    }, [infoAddToCart]);

    const optionQuantityProduct = () => {
        let product = {};
        const currentProduct = (+productDetail.available > 10 || !productDetail.available) ? 10 : +productDetail.available;
        // eslint-disable-next-line no-return-assign
        Array.from(Array(currentProduct).keys()).forEach((item) => product = { ...product, [item + 1]: item + 1 });
        return product;
    };

    const addShoppingCart = () => {
        addProductToCart({ product_id: productDetail.id, quantity: selectQuantity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ isOpen: false, type: '', message: '' });
    };

    const nextProductItem = () => {
        nextProduct();
        setSelectQuantity(1);
    };

    const prevProductItem = () => {
        prevProduct();
        setSelectQuantity(1);
    };

    return (
        <>
            <GridContainer style={{ height: 'auto', alignSelf: 'baseline' }}>
                <GridItem md={7}>
                    <Box className={classes.boxPlaceholder}>
                        {productDetail.media_type === MEDIA_TYPE.IMAGE && (
                            <img
                                src={productDetail.media}
                                alt=""
                                className={classes.imgLightbox}
                                onClick={() => setOpenDialogImg(true)}
                            />
                        )}
                        {productDetail.media_type === MEDIA_TYPE.AUDIO
                            && (
                                <>
                                    <ReactPlayer
                                        controls
                                        config={{
                                            attributes: {
                                                controlsList: 'nodownload',
                                                style: {
                                                    outline: 'none',
                                                    margin: 'auto',
                                                    maxHeight: '400px',
                                                    maxWidth: 'calc(100% - 120px)',
                                                },
                                            },
                                            forceAudio: true,
                                        }}
                                        width="100%"
                                        height="auto"
                                        url={(productDetail.media && token) && `${productDetail.media}?token=${token}`}
                                        style={{ display: 'flex', margin: 'auto' }}
                                    />
                                </>
                            )}
                        {productDetail.media_type === MEDIA_TYPE.VIDEO
                            && (
                                <ReactPlayer
                                    controls
                                    config={{
                                        attributes: {
                                            controlsList: 'nodownload',
                                            style: {
                                                outline: 'none',
                                                margin: 'auto',
                                                maxHeight: '400px',
                                                maxWidth: '100%',
                                            },
                                        },
                                    }}
                                    width="100%"
                                    height="auto"
                                    url={(productDetail.media && token) && `${productDetail.media}?token=${token}`}
                                    style={{ display: 'flex', margin: 'auto' }}
                                />
                            )}
                    </Box>
                </GridItem>
                <GridItem md={5}>
                    <Box fontSize="20px">
                        <CustomTypography className={classes.productDetailName}>
                            {productDetail.name}
                        </CustomTypography>
                        <CustomTypography className={classes.productDescription}>
                            {productDetail.description}
                        </CustomTypography>
                        <div className={classes.priceSection}>
                            <span className={classes.priceLabel}>
                                {intl.formatMessage({ id: 'price' })}
                                :
                            </span>
                            <span>
                                {`${intl.formatNumber(productDetail.price)}${intl.formatMessage({ id: 'common.yen' })}`}
                            </span>
                        </div>
                    </Box>
                    <Box display="flex" alignItems="center" flexWrap="wrap" width="100%">
                        { isWeakAuth === '' && !eventInfo.free_exhibition && eventInfo.has_shopping_cart && eventInfo.grand_zone && (
                            (!productDetail.available) ? (
                                <div className={classes.noProduct}>
                                    SOLD OUT
                                </div>
                            ) : (
                                <div className={(!productDetail?.product_sales_settings) ? classes.chooseNoProduct : classes.chooseProduct}>
                                    <SelectorInput
                                        isCustomColor
                                        customColor={eventInfo?.settings_colors_viewer?.contents_text_color}
                                        valueSelected={selectQuantity}
                                        optionList={optionQuantityProduct()}
                                        onChange={(valueInput) => setSelectQuantity(+valueInput)}
                                    />
                                    <Button
                                        color="danger"
                                        size="sm"
                                        round
                                        className={classes.btnStyle}
                                        onClick={!isPreview
                                            ? () => addShoppingCart()
                                            : () => setSnackbar({
                                                isOpen: true,
                                                type: 'error',
                                                message: intl.formatMessage({ id: 'common.noPreview' }),
                                            })}
                                        disabled={!productDetail?.product_sales_settings}
                                        buttonTextColor={eventInfo?.settings_colors_viewer?.button_text_color}
                                        buttonBackgroundColor={eventInfo?.settings_colors_viewer?.button_background_color?.shopping_button}
                                    >
                                        {customText?.button_name?.add_to_cart || intl.formatMessage({ id: 'exhibition.addCart' })}
                                    </Button>
                                </div>
                            )
                        )}
                        {productDetail.purchase_link && (
                            <Button
                                color="danger"
                                size="sm"
                                round
                                className={classes.btnStyle}
                                href={!isPreview && productDetail.purchase_link}
                                onClick={isPreview ? () => setSnackbar({
                                    isOpen: true,
                                    type: 'error',
                                    message: intl.formatMessage({ id: 'common.noPreview' }),
                                }) : () => {}}
                                target="_blank"
                                buttonTextColor={eventInfo?.settings_colors_viewer?.button_text_color}
                                buttonBackgroundColor={eventInfo?.settings_colors_viewer?.button_background_color?.shopping_button}
                            >
                                {customText?.button_name?.purchase_products || intl.formatMessage({ id: 'productRegister.purchaseLink' })}
                            </Button>
                        )}

                        {productDetail.business_contact && (
                            <Button
                                color="navy"
                                gradient
                                size="sm"
                                round
                                className={classes.btnStyle}
                                onClick={!isPreview ? () => setOpenBusinessContactForm(true) : () => setSnackbar({
                                    isOpen: true,
                                    type: 'error',
                                    message: intl.formatMessage({ id: 'common.noPreview' }),
                                })}
                                buttonTextColor={eventInfo?.settings_colors_viewer?.button_text_color}
                                buttonBackgroundColor={eventInfo?.settings_colors_viewer?.button_background_color?.common_button}
                            >
                                {customText?.button_name?.business_contact || intl.formatMessage({ id: 'productRegister.businessContactNewText' })}
                            </Button>
                        )}

                        {(productDetail.has_outside_link && isAuthorized) && (
                            <Button
                                color="navy"
                                gradient
                                size="sm"
                                round
                                className={classes.btnStyle}
                                href={!isPreview && outsideLinkUrl}
                                onClick={isPreview ? () => setSnackbar({
                                    isOpen: true,
                                    type: 'error',
                                    message: intl.formatMessage({ id: 'common.noPreview' }),
                                }) : () => {}}
                                target="_blank"
                                buttonTextColor={eventInfo?.settings_colors_viewer?.button_text_color}
                                buttonBackgroundColor={eventInfo?.settings_colors_viewer?.button_background_color?.common_button}
                            >
                                {customText?.button_name?.direct_consultation || intl.formatMessage({ id: 'productRegister.externalLink' })}
                            </Button>
                        )}
                    </Box>
                </GridItem>
                <GridItem md={12}>
                    <Box display="flex" justifyContent="center" marginTop="30px">
                        <Button
                            color="navy"
                            gradient
                            size="sm"
                            round
                            className={classes.btnStyle}
                            onClick={() => prevProductItem()}
                            disabled={productActiveIndex === 0}
                            buttonTextColor={eventInfo?.settings_colors_viewer?.button_text_color}
                            buttonBackgroundColor={eventInfo?.settings_colors_viewer?.button_background_color?.common_button}
                        >
                            {customText?.button_name?.previous_product || intl.formatMessage({ id: 'prevProduct' })}
                        </Button>
                        <Button
                            color="navy"
                            gradient
                            size="sm"
                            round
                            className={classes.btnStyle}
                            onClick={() => nextProductItem()}
                            disabled={productActiveIndex === totalProducts - 1}
                            buttonTextColor={eventInfo?.settings_colors_viewer?.button_text_color}
                            buttonBackgroundColor={eventInfo?.settings_colors_viewer?.button_background_color?.common_button}
                        >
                            {customText?.button_name?.next_product || intl.formatMessage({ id: 'nextProduct' })}
                        </Button>
                    </Box>
                </GridItem>
            </GridContainer>

            <ImgDialog
                open={openDialogImg}
                href={productDetail && productDetail.media}
                handleClose={() => setOpenDialogImg(false)}
            />
            <Snackbar
                open={snackbar.isOpen}
                onClose={handleCloseSnackbar}
                message={snackbar.message}
                severity={snackbar.type}
                autoHideDuration={1000}
            />
        </>
    );
};

GrandBoothProductItem.defaultProps = {
    productDetail: {},
    token: '',
    outsideLinkUrl: false,
    infoAddToCart: {},
    addProductToCart: () => {},
    clearAddToCart: () => {},
    isWeakAuth: {},
    eventInfo: {},
    getCount: {},
    customText: {},
    isPreview: false,
};

GrandBoothProductItem.propTypes = {
    productDetail: PropTypes.oneOfType([() => null, PropTypes.object]),
    token: PropTypes.string,
    isAuthorized: PropTypes.bool.isRequired,
    prevProduct: PropTypes.func.isRequired,
    nextProduct: PropTypes.func.isRequired,
    outsideLinkUrl: PropTypes.bool,
    setOpenBusinessContactForm: PropTypes.func.isRequired,
    productActiveIndex: PropTypes.number.isRequired,
    totalProducts: PropTypes.number.isRequired,
    infoAddToCart: PropTypes.oneOfType([() => null, PropTypes.object]),
    addProductToCart: PropTypes.func,
    clearAddToCart: PropTypes.func,
    isWeakAuth: PropTypes.oneOfType([() => null, PropTypes.object]),
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    getCount: PropTypes.func,
    customText: PropTypes.oneOfType([() => null, PropTypes.object]),
    isPreview: PropTypes.bool,
};

export default GrandBoothProductItem;
